import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { hasPermission, manageAnySurveyPermission } from 'unihow-common';
import styles from './frontPage.module.css';

import { langType, surveyTypeValues } from '../../constants/types';
import { withLang } from '../../i18n';
import { getUserSurveys } from '../../api';
import SurveyBoxList from './SurveyBoxList';
import { addErrorMessageAction, setTaskFulfilled, setTaskPending } from '../../actions';
import { autoFocusElementById, requestPending } from '../../util';
import {
  TITLE_PREFIX, routes
} from '../../constants';
import useTitle from '../../hooks/useTitle';
import useCurrentUser from '../../hooks/useCurrentUser';

const initialSurveysState = Object.keys(surveyTypeValues).reduce((prev, curr) => ({
  ...prev,
  [curr]: []
}), {});
const MAIN_TITLE = 'main_title';

const FrontPage = ({
  translate: t
}) => {
  const dispatch = useDispatch();
  const [surveys, setSurveys] = React.useState(initialSurveysState);
  const [hasSurveys, setHasSurveys] = React.useState(false);
  const { permissions: userPermissions } = useCurrentUser();
  const loading = useSelector((state) => requestPending(state));

  const showStartSurveysSection = hasPermission(userPermissions, manageAnySurveyPermission());

  // overrides previous document title value with just UniHow
  useTitle(TITLE_PREFIX);

  React.useEffect(() => {
    autoFocusElementById(MAIN_TITLE);
    dispatch(setTaskPending());
    getUserSurveys()
      .then((page) => {
        setHasSurveys(page.content.length > 0);
        setSurveys(page.content.reduce((prev, curr) => ({
          ...prev,
          [curr.type]: [...prev[curr.type], curr]
        }), initialSurveysState));
      })
      .catch((error) => dispatch(addErrorMessageAction(error)))
      .finally(() => dispatch(setTaskFulfilled()));
  }, []);

  return (
    <div className={styles.container}>
      <div className={`page-container ${styles.content}`}>
        <div className="flex-inline">
          <h1 className="page-title--small" id={MAIN_TITLE}>UniHow</h1>
        </div>
        <p>{t('frontPage.intro')}</p>

        <h2>{t('frontPage.yourSurveys')}</h2>
        {!hasSurveys && !loading && <p>{t('frontPage.youHaveNoSurveys')}</p>}
        {hasSurveys && Object.values(surveyTypeValues).map((surveyType) => (
          surveys[surveyType].length
            ? (
              <SurveyBoxList
                type={surveyType}
                surveys={surveys[surveyType]}
                key={surveyType}
              />
            ) : null
        ))}
      </div>
      {showStartSurveysSection && (
        <div className={styles.startSurveySection}>
          <div className={`page-container ${styles.content}`}>
            <h2>{t('frontPage.wantToStartASurvey')}</h2>
            <p>{t('frontPage.startSurveyInfo')}</p>
            <Link to={routes.surveys.index} className="button">{t('frontPage.startSurvey')}</Link>
          </div>
        </div>
      )}
      <div className={`page-container ${styles.content}`}>
        <h2>{t('frontPage.instructions.title')}</h2>
        <ol className={styles.instructions}>
          {[...Array(3)].map((_, index) => {
            const title = t(`frontPage.instructions.step${index + 1}.title`);
            const desc = t(`frontPage.instructions.step${index + 1}.description`);
            return (
              <li key={title}>
                <h3>{title}</h3>
                <p>{desc}</p>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={`page-container ${styles.content}`}>
        <h2>{t('frontPage.whatIsUniHow')}</h2>
        <div
          className={styles.description}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: t('frontPage.description') }}
        />
      </div>
    </div>
  );
};

FrontPage.propTypes = {
  ...langType
};

export default withLang(FrontPage);
